'use client'

import { useEffect, useState } from 'react';
import { useUser } from '@clerk/nextjs';
import OneSignal from 'react-onesignal';

const DEBUG = false;
let initializationPromise = null;

export default function OneSignalProvider({ children }) {
  const { user, isLoaded } = useUser();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    const appId = process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID;
    if (!appId) {
      console.error('OneSignal initialization skipped: NEXT_PUBLIC_ONESIGNAL_APP_ID is missing');
      return () => { mounted = false; };
    }

    if (!initializationPromise) {
      initializationPromise = OneSignal.init({
        appId: process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: true,
      });
    }

    initializationPromise
      .then(() => {
        if (mounted) setIsInitialized(true);
        if (DEBUG) console.log('OneSignal initialized');
      })
      .catch(err => {
        initializationPromise = null;
        console.error('OneSignal initialization error:', err);
      });

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    // Set external user ID when Clerk user is loaded
    if (isInitialized && isLoaded && user) {
      const linkUser = async () => {
        try {
          await OneSignal.login(user.id);
          if (DEBUG) console.log('OneSignal external user ID set to:', user.id);

          const subscriptions = [];
          const userEmail = user.primaryEmailAddress?.emailAddress;
          const userPhone = user.primaryPhoneNumber?.phoneNumber;
          if (userEmail) subscriptions.push(['email', OneSignal.User.addEmail(userEmail)]);
          if (userPhone) subscriptions.push(['SMS', OneSignal.User.addSms(userPhone)]);

          const results = await Promise.allSettled(subscriptions.map(([, promise]) => promise));
          results.forEach((result, index) => {
            const channel = subscriptions[index][0];
            if (result.status === 'rejected') console.error(`OneSignal ${channel} error:`, result.reason);
            else if (DEBUG) console.log(`OneSignal ${channel} subscription linked`);
          });
        } catch (err) {
          console.error('OneSignal login error:', err);
        }
      };

      linkUser();
    }
  }, [isInitialized, isLoaded, user]);

  return children;
}
